import './Candidate_Interview_Slot_Selected.css';
import Component_Header_Notification from '../Components/Component_Header_Notification';
import { JSX, useEffect, useState } from 'react';
import type { SelectedSlotWithContext } from '../Pages/Candidate_Page1';
import { getInterviewPairings } from '../Requests/Get_Interview_Pairings';
import { getInterviewPairingsExp, InterviewPairingsResponseExp, PairingExp } from '../Requests/Get_Interview_Pairings_Exp';
import Icons_Repository from '../Icons/Icons_Repository';
import { CheckAlternateSlotsEmailSent } from '../Requests/CheckAlternateSlotsEmailSent';
import Component_Popup_Alert from '../Components/Reschedule_Alert_Popup';
import { getInterviewPairingsPanel } from '../Requests/Get_Interview_Pairings_Panel';


interface CandidateInterviewSlotProps {
  slot: any | null;
  candidateEmail: string; 
  setSelectedPage: (page: string) => void;
  setSelectedDates: (date: Date[]) => void;
  setSelectedSlot: (slot: SelectedSlotWithContext | null) => void;
  refreshPairings: () => void;
  onRescheduleClick: () => void;
  onWithdrawClick: () => void;
  isExperienced?: boolean;
  isPanel?: boolean; 
}

function Candidate_Interview_Slot_Selected({ 
  slot, 
  candidateEmail, 
  setSelectedPage ,
  setSelectedDates,
  setSelectedSlot,
  refreshPairings,
  onRescheduleClick,
  onWithdrawClick,
  isExperienced = false,
  isPanel = false
}: CandidateInterviewSlotProps) 
{
    const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const [withdrawing, setWithdrawing] = useState(false);
    const [isAlternateSlotsAvailable, setIsAlternateSlotsAvailable]= useState<boolean>(false);
    const [isAdditionalSlotsRequestEmailSent, setisAdditionalSlotsRequestEmailSent] = useState<boolean>(false);
    const [showRescheduleAlert, setShowRescheduleAlert] = useState(false);


    // NEW: Multi-stage navigation for experienced candidates
    const [allStagesData, setAllStagesData] = useState<InterviewPairingsResponseExp[] | null>(null);
    const [currentStageIndex, setCurrentStageIndex] = useState(0);
    const [currentStageSlot, setCurrentStageSlot] = useState<any>(null);

    // Fetch all stages data for experienced candidates
    useEffect(() => {
      const fetchStagesData = async () => {
        if (!isExperienced || !candidateEmail) return;

        try {
          const data = await getInterviewPairingsExp(false);
          if (data) {
            // Filter only booked stages
            const bookedStages = data.filter(group => group.interview_paired);
            setAllStagesData(bookedStages);
            
            // Set initial stage
            if (bookedStages.length > 0) {
              const initialStage = bookedStages[0];
              setCurrentStageSlot({
                requisition_id: initialStage.requisition_id,
                job_title: initialStage.job_title,
                pairing: initialStage.pairings[0],
                submission_group_id: initialStage.pairings[0].submission_group_id,
                stage: initialStage.pairings[0].stage
              });
            }
          }
        } catch (error) {
          console.error('Error fetching stages data:', error);
        }
      };

      if (isExperienced) {
        fetchStagesData();
      }
    }, [isExperienced, candidateEmail]);

    // Refetch stages data when component receives updated slot prop
useEffect(() => {
  const refetchStagesData = async () => {
    if (!isExperienced || !candidateEmail) return;

    try {
      const data = await getInterviewPairingsExp(false);
      if (data) {
        const bookedStages = data.filter(group => group.interview_paired);
        setAllStagesData(bookedStages);
        
        // Update current stage slot with fresh data
        if (bookedStages.length > 0 && currentStageIndex < bookedStages.length) {
          const stage = bookedStages[currentStageIndex];
          setCurrentStageSlot({
            requisition_id: stage.requisition_id,
            job_title: stage.job_title,
            pairing: stage.pairings[0],
            submission_group_id: stage.pairings[0].submission_group_id,
            stage: stage.pairings[0].stage
          });
        }
      }
    } catch (error) {
      console.error('Error refetching stages data:', error);
    }
  };

  if (isExperienced) {
    refetchStagesData();
  }
}, [slot]); // Refetch when slot prop changes

    // Navigation handlers
    const handlePrevStage = () => {
      if (currentStageIndex > 0 && allStagesData) {
        const newIndex = currentStageIndex - 1;
        setCurrentStageIndex(newIndex);
        const stage = allStagesData[newIndex];
        setCurrentStageSlot({
          requisition_id: stage.requisition_id,
          job_title: stage.job_title,
          pairing: stage.pairings[0],
          submission_group_id: stage.pairings[0].submission_group_id,
          stage: stage.pairings[0].stage
        });
      }
    };

    const handleNextStage = () => {
      if (allStagesData && currentStageIndex < allStagesData.length - 1) {
        const newIndex = currentStageIndex + 1;
        setCurrentStageIndex(newIndex);
        const stage = allStagesData[newIndex];
        setCurrentStageSlot({
          requisition_id: stage.requisition_id,
          job_title: stage.job_title,
          pairing: stage.pairings[0],
          submission_group_id: stage.pairings[0].submission_group_id,
          stage: stage.pairings[0].stage
        });
      }
    };

    // Use currentStageSlot for experienced, regular slot for campus
    const displaySlot = (isExperienced || isPanel) ? currentStageSlot : slot;

    useEffect(() => {
      const sendAlternateSlotsEmailCheck = async () => {
        if (!candidateEmail || !displaySlot) return;

        try {
          const payload = {
            email_type: "reschedule_no_slots_available_already_called",
          };

          const response = await CheckAlternateSlotsEmailSent(payload);
          console.log("Alternate slots email check response:", response);
          
          if (response.message === 'Present') {
            setisAdditionalSlotsRequestEmailSent(true);
          }

          // CRITICAL FIX: Only check alternate slots for CAMPUS candidates
          if (!isExperienced) {
            const data = await getInterviewPairings(candidateEmail, true);

            if (data && Array.isArray(data.pairings) && data.pairings.length === 0) {
              console.log("No other slots");
            } else {
              setIsAlternateSlotsAvailable(true);
              console.log("Other slots present");
            }
          }

        } catch (error) {
          console.error("Failed to check alternate slots email:", error);
        }
      };

      sendAlternateSlotsEmailCheck();
    }, [candidateEmail, displaySlot, isExperienced]);

    // Fetch panel slot data
    useEffect(() => {
      const fetchPanelData = async () => {
        if (!isPanel || !candidateEmail) return;
        try {
          const data = await getInterviewPairingsPanel(false);
          if (data && data.pairings && data.pairings.length > 0 && data.interview_paired) {
            // Find the booked pairing (is_assigned = true or first available)
            const bookedPairing = data.pairings.find((p: any) => p.is_assigned) || data.pairings[0];
            setCurrentStageSlot({
              requisition_id: data.requisition_id,
              job_title: data.job_title,
              pairing: bookedPairing,
            });
          }
        } catch (error) {
          console.error('Error fetching panel slot data:', error);
        }
      };
      fetchPanelData();
    }, [isPanel, candidateEmail, slot]);

    const sanitizeUrl = (url: string | undefined | null): string | null => {
      if (!url) return null;

      try {
        const parsed = new URL(url);
        const allowedProtocols = ['http:', 'https:'];
        if (!allowedProtocols.includes(parsed.protocol)) {
          return null;
        }

        const allowedDomains = ['teams.microsoft.com'];
        const isAllowedDomain = allowedDomains.some(domain => 
          parsed.hostname.endsWith(domain)
        );
        
        if (!isAllowedDomain) {
          console.warn(`Blocked potentially unsafe domain: ${parsed.hostname}`);
          return null;
        }

        return parsed.toString();
      } catch {
        return null;
      }
    };

    const getAbbreviation = () => {
      try {
        const abbr = new Date().toLocaleTimeString('en-us', {
          timeZone: browserTimeZone,
          timeZoneName: 'short',
        }).split(' ').pop();

        if (abbr && /^[A-Z]{2,4}$/.test(abbr)) {
          return abbr;
        }

        const knownZones: Record<string, string> = {
          'Asia/Kolkata': 'IST',
          'Asia/Calcutta': 'IST',
          'America/New_York': 'EST',
          'America/Los_Angeles': 'PST',
          'America/Chicago': 'CST',
          'America/Denver': 'MST',
          'Europe/London': 'GMT',
          'Europe/Paris': 'CET',
          'Europe/Berlin': 'CET',
          'Asia/Dubai': 'GST',
          'Asia/Tokyo': 'JST',
          'Australia/Sydney': 'AEDT',
          'Australia/Melbourne': 'AEDT',
        };

        return knownZones[browserTimeZone] || abbr || browserTimeZone;
      } catch {
        return 'UTC';
      }
    };

    const toLongDate = (ymd: string): JSX.Element | string => {
      if (!ymd) return ymd;

      const [year, month, day] = ymd.split('-').map(Number);
      const date = new Date(year, month - 1, day);

      const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];

      const getOrdinalSuffix = (n: number): JSX.Element => {
        let suffix = 'th';
        if (n < 11 || n > 13) {
          switch (n % 10) {
            case 1: suffix = 'st'; break;
            case 2: suffix = 'nd'; break;
            case 3: suffix = 'rd'; break;
          }
        }
        return <>{n}</>;
      };

      const weekday = weekdays[date.getDay()];
      const monthName = months[date.getMonth()];
      const dayWithSuffix = getOrdinalSuffix(date.getDate());

      return (
        <>
          {weekday}, {monthName} {dayWithSuffix}, {year}
        </>
      );
    };

    if (!displaySlot) return null;

    const dStart = new Date(displaySlot.pairing.start_time);
    const dEnd = new Date(displaySlot.pairing.end_time);
    const dateYMD = dStart.toLocaleDateString('en-CA', { timeZone: browserTimeZone });
    const startStr = dStart.toLocaleTimeString('en-US', {
      timeZone: browserTimeZone, hour: 'numeric', minute: '2-digit', hour12: true
    });
    const endStr = dEnd.toLocaleTimeString('en-US', {
      timeZone: browserTimeZone, hour: 'numeric', minute: '2-digit', hour12: true
    });

    const timeRange = `${startStr} – ${endStr}`;
    const safeMeetingLink = sanitizeUrl(displaySlot?.pairing?.meeting_link);

    // Get stage display name
    const getStageDisplayName = () => {
      if (!isExperienced || !displaySlot?.pairing) return '';
      const type = displaySlot.pairing.interview_1_type || 'Interview';
      return type;
    };

    return (
      <div className='candidate-selected-interview-slot'>
        <div className='candidate-selected-interview-slot-parent-container'>
          <div className='parent-container-header-component'>
            <Component_Header_Notification message='Hi, you have an upcoming interview!' pairingsData={null}/>
          </div>
          <div className='interview-slot-details-container'>
            <div className='interview-slot-details-role-info-card'>
                <div className='slot-details-header'>
                  <div className='slot-details-header-text'>
                    Interview details
                  </div>
                  {/* Navigation controls for experienced candidates */}
                  {isExperienced && !isPanel && allStagesData && allStagesData.length > 1 && (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
      <div className='stage-navigation-controls'>
        <button
          className='nav-arrow-button'
          onClick={handlePrevStage}
          disabled={currentStageIndex === 0}
        >
          <Icons_Repository iconName="chevron_left" />
        </button>
        <span className='stage-name'>{getStageDisplayName()}</span>
        <button
          className='nav-arrow-button'
          onClick={handleNextStage}
          disabled={currentStageIndex === allStagesData.length - 1}
        >
          <Icons_Repository iconName="chevron_right" />
        </button>
      </div>
      <span className='stage-navigation-tooltip'>Use the arrows to navigate between interviews and view their details</span>
    </div>
  )}
                </div>
                <div className='slot-details-body'>
                  <div className='job-title-section'>
                    <div className='job-title-logo'>
                      <div className='job-title-logo-icon'>
                        <Icons_Repository iconName="users" />
                      </div>
                    </div>
                    <div className='job-title-text'>
                      <span><strong>Job title:</strong> {displaySlot.job_title}</span>
                    </div>
                  </div>
                  <div className='status-section'>
                    <div className='status-section-logo'>
                      <div className='status-section-logo-icon'>
                        <Icons_Repository iconName="check_circle" />
                      </div>
                    </div>
                    <div className='status-section-text'>
                      <span><strong>Status:</strong> Interview scheduled</span>
                    </div>
                  </div>
                  <div className='meeting-link-section'>
                    <div className='meeting-link-logo'>
                      <div className='meeting-link-logo-icon'>
                        <Icons_Repository iconName="link" />
                      </div>
                    </div>
                    <div className='meeting-link-text'>
                      <span>
                        <strong>Meeting link: </strong>
                        {safeMeetingLink ? (
                          <a
                            href={safeMeetingLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#FD5105', textDecoration: 'none' }}
                          >
                            Click here
                          </a>
                        ) : (
                          <span>Not available</span>
                        )}
                      </span>
                    </div>
                  </div>
                  <div className='date-time-section'>
                    <div className='date-time-logo'>
                      <div className='date-time-logo-icon'>
                        <Icons_Repository iconName="calendar" />
                      </div>
                    </div>
                    <div className='date-time-text'>
                      <span>
                        <strong>Date and time:</strong>{' '}
                        <span>
                          {toLongDate(dateYMD)} at {timeRange} {getAbbreviation()}
                          {isAdditionalSlotsRequestEmailSent && !isAlternateSlotsAvailable && (
                            <span style={{ color: '#FD5105', fontWeight: 200 }}>
                              {' '}(Additional slots requested)
                            </span>
                          )}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
            </div>
            <div className='refresh-tip-container'>
          <span className='refresh-tip-text'>Note: If you do not see your latest selections, please refresh the page</span>
        </div>

            <div className='reschedule-withdraw-application-parent-container'>
              <button 
                className='reschedule-application-button'
                disabled={withdrawing}
                onClick={() => {
                  // Pass current stage info to parent for reschedule
                  if ((isExperienced || isPanel) && currentStageSlot) {
                    console.log('Setting selected slot for reschedule:', currentStageSlot);
                    setSelectedSlot(currentStageSlot);
                    // Small delay to ensure state updates before opening popup
                    setTimeout(() => {
                      onRescheduleClick();
                    }, 50);
                  } else {
                    const interviewStart = new Date(slot?.pairing?.start_time);
                    const now = new Date();
                    const hoursUntilInterview = (interviewStart.getTime() - now.getTime()) / (1000 * 60 * 60);
                    if (hoursUntilInterview <= 48) {
                      setShowRescheduleAlert(true);
                      return;
                    }
                    onRescheduleClick();
                  }
                }}
              >
                Reschedule interview
              </button>
              <button 
                className='withdraw-application-button'
                disabled={withdrawing}
                onClick={onWithdrawClick}
              >
                Withdraw from Interview
              </button>
            </div>
          </div>
          
        </div>
        <Component_Popup_Alert
          isOpen={showRescheduleAlert}
          onClose={() => setShowRescheduleAlert(false)}
          title="Reschedule Not Allowed"
          message="Interviews cannot be rescheduled within 48 hours of the scheduled start time."
        />

      </div>
    );
}

export default Candidate_Interview_Slot_Selected;